import { cn } from '@/shared/helpers/classnames'

import type { FC } from 'react'

import type { InputLabelProps } from './types'

/**
 * InputLabel component for labeling form inputs.
 *
 * Wraps the input element and its label content together.
 * Can be used with various input types: Checkbox, Toggle, RadioGroup.Option, etc.
 * Provides consistent styling and layout across all input types.
 *
 * @example
 * Usage with Checkbox:
 * ```tsx
 * <InputLabel>
 *   <Checkbox checked={value} onChange={handleChange} />
 *   <span>Accept terms</span>
 * </InputLabel>
 * ```
 *
 * @example
 * Usage with Toggle:
 * ```tsx
 * <InputLabel>
 *   <Toggle checked={value} onChange={handleChange} />
 *   <span>Enable notifications</span>
 * </InputLabel>
 * ```
 *
 * @example
 * Complex content:
 * ```tsx
 * <InputLabel>
 *   <Checkbox checked={value} onChange={handleChange} />
 *   <div>
 *     <div className="font-bold">Premium Plan</div>
 *     <div className="text-sm">$29.99/month</div>
 *   </div>
 * </InputLabel>
 * ```
 *
 * @example
 * With RadioGroup (InputLabel wraps both RadioItem and content):
 * ```tsx
 * <RadioGroup.Option value="premium">
 *   <InputLabel>
 *     <div>
 *       <strong>Premium Plan</strong>
 *       <span>$29.99/month</span>
 *     </div>
 *   </InputLabel>
 * </RadioGroup.Option>
 * ```
 */
export const InputLabel: FC<InputLabelProps> = ({ children, disabled, ...props }) => {
  return (
    <label
      className={cn('flex cursor-pointer items-center gap-2 leading-none', {
        'cursor-not-allowed opacity-50': disabled,
      })}
      {...props}
    >
      {children}
    </label>
  )
}

InputLabel.displayName = 'InputLabel'
